import { useState, useEffect, useCallback, useRef } from "react";
import Swal from "sweetalert2";
import {
  collection,
  getDocs,
  serverTimestamp,
  doc,
  updateDoc,
  addDoc,
  arrayUnion,
  getDoc,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { v4 as uuidv4 } from "uuid";
import Nav from "./Nav";
import Sidebar from "./Sidebar";
import TablaPedidos from "./TablaPedidos";
import Modal from "react-modal";
import DatePicker, { registerLocale } from "react-datepicker";
import es from "date-fns/locale/es";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import PreviaPedidos from "./PreviaPedidos";
import { useNavContext } from "../context/NavContext";
import useProductos from "../hooks/useProductos";
import useArchivos from "../hooks/useArchivos";

moment.locale("es");
registerLocale("es", es);

// Establece la función de inicialización de react-modal para evitar un aviso de desenfoque de accesibilidad
Modal.setAppElement("#root");

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    border: "none",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    padding: "20px",
    maxWidth: "800px",
    width: "100%",
    height: "500px",
    maxHeight: "90vh",
    overflow: "auto",
    fontFamily: "Roboto, sans-serif", // Aplica la fuente Roboto
  },
  label: {
    display: "block",
    marginBottom: "10px",
  },
  input: {
    width: "100%",
    padding: "8px",
    marginBottom: "20px",
    borderRadius: "5px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },
  button: {
    width: "50%", // Centra el botón de agregar
    padding: "10px",
    borderRadius: "5px",
    backgroundColor: "#007bff",
    color: "#fff",
    border: "none",
    cursor: "pointer",
    fontSize: "16px",
    display: "flex",
    justifyContent: "center",
    margin: "0 auto",
    fontWeight: "700",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
};

function MiniSelect({ value, onChange, options, width = 64, height = 40 }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative", width, height }}>
      {/* Botón visible */}
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          borderLeft: "1px solid #ddd",
          background: "#fff", // mismo color que el input
          fontSize: 14,
          fontWeight: 500,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderTopRightRadius: 8,
          borderBottomRightRadius: 8,
          boxShadow: "inset 0 0 0 1px #ddd", // asegura borde cerrado visualmente
          color: "#111",
          padding: 0,
        }}
      >
        <span
          style={{
            position: "relative",
            top: "1px", // centra verticalmente
          }}
        >
          {value}
        </span>
        <i
          className="bi bi-caret-down-fill"
          style={{
            marginLeft: 6,
            fontSize: 12,
            color: "#666",
            position: "relative",
            top: "1px",
          }}
        />
      </button>

      {/* Menú desplegable */}
      {open && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            zIndex: 1000,
            minWidth: 100,
            background: "#fff",
            border: "1px solid #e7e7ea",
            borderRadius: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,.12)",
            padding: 6,
          }}
        >
          {options.map((opt) => {
            const selected = value === opt.value;
            return (
              <div
                key={opt.value}
                role="option"
                aria-selected={selected}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                style={{
                  padding: "8px 12px",
                  borderRadius: 10,
                  fontSize: 14,
                  textAlign: "center",
                  cursor: "pointer",
                  background: selected ? "#eaf1ff" : "transparent",
                  color: selected ? "#0a66ff" : "#111",
                  transition: "background 0.15s",
                }}
              >
                {opt.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PedidoForm(props) {
  const { orden, closeModal, cotizacion } = props;
  const [productos, setProductos] = useState([]);
  const {
    datos,
    descuento,
    añadirFila,
    eliminar,
    handleInputChange,
    filteredProductos,
    activeRow,
    obtenerIVA,
    calcularSubtotal,
    setDatos,
    setFilteredProductos,
    agregarBulk,
    setDescuento,
  } = useProductos(
    orden?.productosSeleccionados || [],
    orden?.descuento || 0,
    productos
  );
  const {
    archivos,
    initArchivos,
    cargando,
    manejarArchivos,
    eliminarArchivo,
    descargarArchivo,
    obtenerIcono,
  } = useArchivos("pedidos");
  const [divisa] = useState("MXN");
  const [cliente, setCliente] = useState("");
  const [clientes, setClientes] = useState([]);
  const [usuario, setUsuario] = useState("");
  const [asunto, setAsunto] = useState("");
  const [fechaVencimiento, setFechaVencimiento] = useState("");
  const [mostrarTabla, setMostrarTabla] = useState(false);
  const [mostrarPrevia, setMostrarPrevia] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [ordenes, setOrdenes] = useState([]);
  const [formDirty, setFormDirty] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [numeroOrdenDeCompra, setNumeroOrdenDeCompra] = useState("");
  const [numeroCotizacion, setNumeroCotizacion] = useState("");
  const [fechaDeOrden, setFechaDeOrden] = useState(new Date());
  const [notas, setNotas] = useState("");
  const { proximasAVencer, proximosEventos } = useNavContext();
  const [terminos, setTerminos] = useState("");
  const [referencia, setReferencia] = useState("");
  const [fechaCotizacion, setFechaCotizacion] = useState(new Date());
  const [planta, setPlanta] = useState("");
  const [area, setArea] = useState("");
  const [rangoFechas, setRangoFechas] = useState([null, null]);
  const [startDate, endDate] = rangoFechas;
  const [division, setDivision] = useState(orden?.division || "");
  const [originalData, setOriginalData] = useState(null);
  const [atencion, setAtencion] = useState("");
  const [correo, setCorreo] = useState("");
  const [envioMetodo, setEnvioMetodo] = useState("");
  const [envioPaqueteria, setEnvioPaqueteria] = useState("");
  const [envioFecha, setEnvioFecha] = useState(null);
  const [envioNumeroGuia, setEnvioNumeroGuia] = useState("");
  const [envioDireccion, setEnvioDireccion] = useState("");
  const [tipoDescuentoGlobal, setTipoDescuentoGlobal] = useState("%"); // "% | MXN"

  useEffect(() => {
    setDatos((prev) =>
      prev.map((p) => ({
        ...p,
        // si por alguna razón viene sin descripcion pero sí con nombre, lo reflejamos
        descripcion: p.descripcion ?? p.nombre ?? "",
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datos.length]); // se dispara en altas/bajas de filas sin ciclar en cada tecla

  // Sincroniza automáticamente los archivos al documento actual
  useEffect(() => {
    if (!orden?.id || archivos.length === 0) return;

    const ref = doc(db, "pedidos", orden.id);
    updateDoc(ref, {
      archivos: archivos.map(({ nombre, url }) => ({ nombre, url })),
    }).catch((err) => console.error("Error al actualizar archivos:", err));
  }, [orden?.id, archivos]);

  const hidratarDesdePedido = useCallback(
    (d) => {
      if (!d) return;

      setNumeroOrdenDeCompra(d.numeroOrdenDeCompra || "");
      setNumeroCotizacion(d.numeroCotizacion || "");
      setReferencia(d.referencia || "");
      setFechaCotizacion(
        d.fechaCotizacion ? new Date(d.fechaCotizacion) : new Date()
      );
      setFechaDeOrden(d.fechaOrden ? new Date(d.fechaOrden) : new Date());
      setFechaVencimiento(
        d.fechaVencimiento ? new Date(d.fechaVencimiento) : null
      );
      setDivision(d.division || "");
      setPlanta(d.planta || "");
      setArea(d.area || "");
      setAsunto(d.asunto || "");
      setUsuario(d.usuario || "");
      setTerminos(d.terminos || "");
      setAtencion(d.atencion || "");
      setCorreo(d.correo || d.email || "");

      const env = d.envio || {};
      setEnvioMetodo(env.metodo || "");
      setEnvioPaqueteria(env.paqueteria || "");
      setEnvioFecha(env.fechaEnvio ? new Date(env.fechaEnvio) : null);
      setEnvioNumeroGuia(env.numeroGuia || "");
      setEnvioDireccion(env.direccion || "");

      setDatos(
        (d.productosSeleccionados || []).map((p) => ({
          id: uuidv4(),
          ...p,
          descripcion: p.descripcion ?? p.nombre ?? "",
          numeroDeParteCliente: p.numeroDeParteCliente || "",
        }))
      );

      setDescuento(parseFloat(d.descuento) || 0);
      setTipoDescuentoGlobal(d.descuentoTipo || "%");

      setOriginalData({
        cliente: d.cliente,
        nombreCliente: d.nombreCliente,
        numeroOrdenDeCompra: d.numeroOrdenDeCompra,
        numeroCotizacion: d.numeroCotizacion,
        fechaCotizacion: d.fechaCotizacion ?? null,
        fechaOrden: d.fechaOrden,
        fechaVencimiento: d.fechaVencimiento ?? null,
        division: d.division,
        area: d.area,
        planta: d.planta,
        asunto: d.asunto,
        usuario: d.usuario,
        referencia: d.referencia,
        terminos: d.terminos,
        notas: d.notas,
        productosSeleccionados: d.productosSeleccionados,
        descuento: d.descuento,
        total: d.total,
        atencion: d.atencion,
        correo: d.correo,
        envio: {
          metodo: env.metodo || "",
          paqueteria: env.paqueteria || "",
          fechaEnvio: env.fechaEnvio || null,
          numeroGuia: env.numeroGuia || "",
          direccion: env.direccion || "",
        },
      });
    },
    [setDatos, setDescuento]
  );

  useEffect(() => {
    if (!orden?.id) return;

    const ref = doc(db, "pedidos", orden.id);

    getDoc(ref).then((snap) => {
      if (snap.exists() && !formDirty) hidratarDesdePedido(snap.data());
    });

    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      if (!formDirty) hidratarDesdePedido(snap.data());
    });

    return () => unsub();
  }, [orden?.id, formDirty, hidratarDesdePedido]);

  useEffect(() => {
    if (orden?.id) {
      const ref = doc(db, "pedidos", orden.id);

      const unsub = onSnapshot(ref, (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();

        // Si hay archivos guardados, los cargamos en el hook
        if (data.archivos && Array.isArray(data.archivos)) {
          initArchivos(data.archivos);
        }
      });

      return () => unsub();
    }
  }, [orden?.id, initArchivos]);

  useEffect(() => {
    // Helper para asignar cliente por ID o por nombre
    const asignarCliente = (id, nombreCliente) => {
      if (id) {
        setCliente(id);
      } else if (nombreCliente) {
        const encontrado = clientes.find(
          (c) => c.empresa.toLowerCase() === nombreCliente.toLowerCase()
        );
        setCliente(encontrado ? encontrado.id : "");
      } else {
        setCliente("");
      }
    };

    // Fuente original (puede ser cotización o una orden existente)
    const fuente = cotizacion || orden;
    if (fuente) {
      setModoEdicion(true);

      // Llenamos TODOS los campos del formulario
      asignarCliente(fuente.cliente, fuente.nombreCliente);
      initArchivos(fuente.archivos || []);
      setReferencia(fuente.referencia || "");
      setFechaCotizacion(
        fuente.fechaCotizacion ? new Date(fuente.fechaCotizacion) : new Date()
      );
      setFechaDeOrden(
        fuente.fechaOrden ? new Date(fuente.fechaOrden) : new Date()
      );
      setFechaVencimiento(
        fuente.fechaVencimiento ? new Date(fuente.fechaVencimiento) : null
      );

      if (orden) {
        setRangoFechas([
          orden.fechaInicioRango ? new Date(orden.fechaInicioRango) : null,
          orden.fechaFinRango ? new Date(orden.fechaFinRango) : null,
        ]);
        setNumeroOrdenDeCompra(orden.numeroOrdenDeCompra || "");
      }

      setDivision(fuente.division || "");
      setDatos(
        (fuente.productosSeleccionados || []).map((p) => ({
          id: uuidv4(),
          ...p,
          descripcion: p.descripcion ?? p.nombre ?? "",
          numeroDeParteCliente: p.numeroDeParteCliente || "",
        }))
      );
      setDescuento(parseFloat(fuente.descuento) || 0);
      setTipoDescuentoGlobal(fuente.descuentoTipo || "%");

      setPlanta(fuente.planta || "");
      setUsuario(fuente.usuario || "");
      setArea(fuente.area || "");
      setAsunto(fuente.asunto || "");
      setNumeroCotizacion(fuente.numeroCotizacion || "");
      setNotas(fuente.notas || "");
      setTerminos(fuente.terminos || "");
      setAtencion(fuente.atencion || "");
      setCorreo(fuente.correo || fuente.email || "");
      const env = fuente.envio || {};
      setEnvioMetodo(env.metodo || "");
      setEnvioPaqueteria(env.paqueteria || "");
      setEnvioFecha(env.fechaEnvio ? new Date(env.fechaEnvio) : null);
      setEnvioNumeroGuia(env.numeroGuia || "");
      setEnvioDireccion(env.direccion || "");

      // ——> AQUÍ capturamos el snapshot ORIGINAL, directamente de 'fuente'
      setOriginalData({
        cliente: fuente.cliente,
        nombreCliente: fuente.nombreCliente,
        numeroOrdenDeCompra: fuente.numeroOrdenDeCompra,
        numeroCotizacion: fuente.numeroCotizacion,
        fechaCotizacion: fuente.fechaCotizacion ?? null,
        fechaOrden: fuente.fechaOrden,
        fechaVencimiento: fuente.fechaVencimiento ?? null,
        division: fuente.division,
        area: fuente.area,
        planta: fuente.planta,
        asunto: fuente.asunto,
        usuario: fuente.usuario,
        atencion: fuente.atencion,
        correo: fuente.correo,
        referencia: fuente.referencia,
        terminos: fuente.terminos,
        notas: fuente.notas,
        productosSeleccionados: fuente.productosSeleccionados,
        descuento: fuente.descuento,
        total: fuente.total,
        envio: {
          metodo: env.metodo || "",
          paqueteria: env.paqueteria || "",
          fechaEnvio: env.fechaEnvio || null,
          numeroGuia: env.numeroGuia || "",
          direccion: env.direccion || "",
        },
      });
    } else {
      // Nuevo formulario: estado inicial
      setModoEdicion(false);
      setDatos([
        {
          id: uuidv4(),
          nombre: "",
          descripcion: "",
          modelo: "",
          numeroDeParteCliente: "",
          cantidad: 0,
          tarifa: 0,
          iva: 0,
          descuento: 0,
          tipoDescuento: "%",
          importe: 0,
        },
      ]);
      setDescuento(0);
      setDivision("");
      initArchivos([]);
      setAtencion("");
      setCorreo("");
      setEnvioMetodo("");
      setEnvioPaqueteria("");
      setEnvioFecha(null);
      setEnvioNumeroGuia("");
      setEnvioDireccion("");
    }
  }, [cotizacion, orden, clientes, initArchivos, setDatos, setDescuento]);

  useEffect(() => {
    // Cargar clientes y productos una sola vez al montar
    const fetchClientesYProductos = async () => {
      try {
        // 1) Cargar clientes
        const clientesSnap = await getDocs(collection(db, "clientes"));
        const clientesList = clientesSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        // Filtrar únicos por empresa
        const clientesUnicos = clientesList.filter(
          (c, i, arr) => arr.findIndex((x) => x.empresa === c.empresa) === i
        );
        setClientes(clientesUnicos);

        // 2) Cargar productos
        const productosSnap = await getDocs(collection(db, "productos"));
        const productosList = productosSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          tipoDescuento: "cantidad",
          valorDescuento: 0,
        }));
        setProductos(productosList);
      } catch (error) {
        console.error("Error al obtener clientes o productos:", error);
      }
    };

    fetchClientesYProductos();
  }, []);

  const handleCancel = () => {
    if (formDirty) {
      Swal.fire({
        title: "¿Seguro que quieres salir?",
        text: "Se perderán los cambios realizados",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, salir",
        cancelButtonText: "Cancelar",
      }).then((result) => {
        if (result.isConfirmed) {
          // Cerrar el modal usando la función pasada como prop
          closeModal();
          setFormDirty(false); // Opcional: resetea el estado si es necesario
          setMostrarTabla(true); // Mostrar la tabla al salir
        }
      });
    } else {
      // Si no hay cambios, simplemente cierra el modal
      closeModal();
      setMostrarTabla(true); // Mostrar la tabla al salir
    }
  };

  const cerrarModalPrevia = () => {
    setMostrarPrevia(false);
    setModalIsOpen(false);
  };

  const continuarDesdePrevia = () => {
    setMostrarTabla(true);
  };

  const calcularTotales = useCallback(() => {
    // Subtotal base de la orden (ya incluye descuentos por fila)
    const subtotal = calcularSubtotal();
    const ivaRate = obtenerIVA(); // p.ej., 0.16

    const dNum = parseFloat(descuento || 0) || 0;
    const descuentoAplicado =
      tipoDescuentoGlobal === "%"
        ? subtotal * (dNum / 100)
        : Math.min(dNum, subtotal);

    const subtotalConDescLocal = Math.max(subtotal - descuentoAplicado, 0);
    const ivaLocal = subtotalConDescLocal * ivaRate;
    const totalLocal = subtotalConDescLocal + ivaLocal;

    return {
      subtotal,
      descuentoAplicado,
      subtotalConDescLocal,
      ivaLocal,
      totalLocal,
      ivaRate,
    };
  }, [calcularSubtotal, obtenerIVA, tipoDescuentoGlobal, descuento]);

  const guardar = async (e) => {
    e.preventDefault();

    // 1) Validación básica
    if (!cliente || !numeroOrdenDeCompra || !fechaDeOrden || !division) {
      return Swal.fire({
        icon: "error",
        title: "Campos obligatorios faltantes",
        text: "Por favor completa los campos: Cliente, Número de Orden y Fecha de Orden.",
      });
    }

    // 1.1) Validación de correo (si se capturó)
    if (correo && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo)) {
      return Swal.fire({
        icon: "error",
        title: "Correo inválido",
        text: "Revisa el formato del correo.",
      });
    }

    try {
      // 2) Verificar duplicados
      const snapshot = await getDocs(collection(db, "pedidos"));
      const dup = snapshot.docs.find(
        (d) => d.data().numeroOrdenDeCompra === numeroOrdenDeCompra
      );
      if (dup && (!modoEdicion || (orden?.id && orden.id !== dup.id))) {
        return Swal.fire({
          icon: "error",
          title: "Número de orden duplicado",
          text: `El número "${numeroOrdenDeCompra}" ya existe.`,
        });
      }

      // 3) Nombre del cliente
      const sel = clientes.find((c) => c.id === cliente);
      const nombreCliente = sel?.empresa || "";

      // 4) Archivos
      const archivosURLs = archivos.map(({ nombre, url }) => ({ nombre, url }));

      // 5) Construir objeto ordenData (tanto para create como update)
      const ordenData = {
        cliente,
        nombreCliente,
        numeroOrdenDeCompra,
        numeroCotizacion,
        fechaCotizacion: fechaCotizacion?.toISOString() || null,
        fechaOrden: fechaDeOrden.toISOString(),
        fechaVencimiento:
          fechaVencimiento instanceof Date
            ? fechaVencimiento.toISOString()
            : null,
        estado: "Pendiente",
        divisa,
        fechaInicioRango: startDate?.toISOString() || null,
        fechaFinRango: endDate?.toISOString() || null,
        notas,
        usuario,
        atencion,
        correo,
        planta,
        division,
        area,
        referencia,
        asunto,
        terminos,
        productosSeleccionados: datos,
        total: totalLocal,
        descuento,
        descuentoTipo: tipoDescuentoGlobal,
        createdAt: serverTimestamp(),
        archivos: archivosURLs,
        envio: {
          metodo: envioMetodo || null,
          paqueteria: envioPaqueteria || null,
          fechaEnvio: envioFecha ? envioFecha.toISOString() : null,
          numeroGuia: envioNumeroGuia || null,
          direccion: envioDireccion || null,
        },
        actionLog: [
          {
            id: uuidv4(),
            timestamp: new Date().toISOString(),
            user: usuario,
            action: "Creó orden",
            entity: `#${numeroOrdenDeCompra}`,
            details: asunto || "",
          },
        ],
      };

      // 6) CREATE o UPDATE
      if (modoEdicion && orden?.id) {
        const refPedido = doc(db, "pedidos", orden.id);

        // 1) Reconstruir los nuevos valores en el mismo shape:
        const newData = {
          cliente,
          nombreCliente: clientes.find((c) => c.id === cliente)?.empresa || "",
          numeroOrdenDeCompra,
          numeroCotizacion,
          fechaCotizacion: fechaCotizacion?.toISOString() || null,
          fechaOrden: fechaDeOrden.toISOString(),
          fechaVencimiento: fechaVencimiento?.toISOString() || null,
          division,
          area,
          planta,
          asunto,
          atencion,
          correo,
          usuario,
          referencia,
          terminos,
          notas,
          productosSeleccionados: datos,
          total: totalLocal,
          descuento,
          descuentoTipo: tipoDescuentoGlobal,
          envio: {
            metodo: envioMetodo || "",
            paqueteria: envioPaqueteria || "",
            fechaEnvio: envioFecha ? envioFecha.toISOString() : null,
            numeroGuia: envioNumeroGuia || "",
            direccion: envioDireccion || "",
          },
        };

        // 2) Comparar originalData vs newData campo a campo:
        const cambios = [];
        Object.keys(newData).forEach((key) => {
          const antes = JSON.stringify(originalData?.[key] ?? "");
          const despues = JSON.stringify(newData[key] ?? "");
          if (antes !== despues) {
            // limpio las comillas de JSON.stringify
            const cleanAntes = antes.replace(/^"|"$/g, "");
            const cleanDespues = despues.replace(/^"|"$/g, "");
            cambios.push(`${key}: ${cleanAntes} → ${cleanDespues}`);
          }
        });
        const details = cambios.join("; ");

        // 3) Actualizar los datos
        await updateDoc(refPedido, newData);

        // trae el doc actualizado y refresca estado local
        const snap = await getDoc(refPedido);
        if (snap.exists()) {
          const updatedOrder = { id: snap.id, ...snap.data() };

          // fuerza actualización en la tabla padre al instante
          props.setOrdenes?.((prev) =>
            prev.map((o) => (o.id === updatedOrder.id ? updatedOrder : o))
          );

          hidratarDesdePedido(updatedOrder);
        }

        setOriginalData(newData); // evita que el diff registre cambios fantasma
        props.onUpdated?.(orden.id, { id: orden.id, ...newData }); // si el padre implementa esto, actualiza su lista al instante

        // 4) Registrar el eventLog con los detalles
        await updateDoc(refPedido, {
          actionLog: arrayUnion({
            id: uuidv4(),
            timestamp: new Date().toISOString(),
            user: usuario,
            action: "Editó orden",
            entity: `#${numeroOrdenDeCompra}`,
            details, // aquí metemos la cadena con los cambios
          }),
        });

        await Swal.fire({
          icon: "success",
          title: "Orden actualizada correctamente",
          showConfirmButton: false,
          timer: 1500,
        });
      } else {
        // creación
        const docRef = await addDoc(collection(db, "pedidos"), ordenData);

        // trae el doc recién creado y fuerza actualización en la tabla padre al instante
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const newOrder = { id: docRef.id, ...snap.data() };

          // evita duplicar si ya llegó por onSnapshot
          props.setOrdenes?.((prev) =>
            prev.some((o) => o.id === newOrder.id) ? prev : [newOrder, ...prev]
          );
        }

        await Swal.fire({
          icon: "success",
          title: "Orden guardada correctamente",
          showConfirmButton: false,
          timer: 1500,
        });
      }

      // 7) RESET + CIERRE
      setFormDirty(false);
      setDatos([
        {
          id: uuidv4(),
          nombre: "",
          descripcion: "",
          modelo: "",
          numeroDeParteCliente: "",
          cantidad: 0,
          tarifa: 0,
          iva: 0,
          descuento: 0,
          tipoDescuento: "%",
          importe: 0,
        },
      ]);
      setDescuento(0);
      setCliente("");
      setNumeroOrdenDeCompra("");
      setFechaDeOrden(new Date());
      setNotas("");
      setTerminos("");
      setAtencion("");
      setCorreo("");
      setEnvioMetodo("");
      setEnvioPaqueteria("");
      setEnvioFecha(null);
      setEnvioNumeroGuia("");
      setEnvioDireccion("");
      closeModal();
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Error al guardar la orden",
        text: err.message,
      });
    }
  };

  const {
    subtotal,
    descuentoAplicado,
    subtotalConDescLocal,
    ivaLocal,
    totalLocal,
    ivaRate,
  } = calcularTotales();

  return (
    <>
      <Nav
        proximasAVencer={proximasAVencer}
        proximosEventos={proximosEventos}
      />

      <Sidebar />

      <div className="cotizacion-form">
        <form
          onKeyDown={(e) => {
            // Solo bloquear "Enter" fuera de los elementos <textarea>
            if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
              e.preventDefault();
            }
          }}
        >
          <div>
            <div className="group apartado">
              <label htmlFor="cliente" className="required">
                Cliente*
              </label>
              <select
                id="cliente"
                name="cliente"
                value={cliente}
                onChange={(e) => {
                  setCliente(e.target.value);
                  const c = clientes.find((x) => x.id === e.target.value);
                  if (c) {
                    setAtencion(c.atencion || "");
                    setCorreo(c.correo || c.email || "");
                  }
                  setFormDirty(true);
                }}
              >
                <option value="">Seleccionar cliente</option>
                {clientes.length > 0 ? (
                  clientes
                    .slice()
                    .sort((a, b) => a.empresa.localeCompare(b.empresa)) // Ordenar alfabéticamente
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.empresa}
                      </option>
                    ))
                ) : (
                  <option value="" disabled>
                    No hay clientes disponibles
                  </option>
                )}
              </select>
            </div>

            <br />

            <div className="group fechas">
              <div className="group">
                <label htmlFor="numeroCotizacionOC">No. Cotización</label>
                <div>
                  <input
                    type="text"
                    id="numeroCotizacionOC"
                    name="numeroCotizacionOC"
                    value={numeroCotizacion}
                    onChange={(e) => {
                      setNumeroCotizacion(e.target.value);
                      setFormDirty(true); // Marcar que el formulario ha sido modificado
                    }}
                  />
                </div>
              </div>

              <div className="group">
                <label htmlFor="numeroOrdenDeCompra" className="required">
                  No. Orden de Compra#*
                </label>
                <input
                  style={{ width: "40%" }}
                  type="text"
                  id="numeroOrdenDeCompra"
                  name="numeroOrdenDeCompra"
                  value={numeroOrdenDeCompra}
                  onChange={(e) => {
                    setNumeroOrdenDeCompra(e.target.value);
                    setFormDirty(true); // Marcar que el formulario ha sido modificado
                  }}
                />
              </div>
            </div>

            <div className="group">
              <label htmlFor="referencia">Descripción</label>
              <input
                type="text"
                id="referencia"
                name="referencia"
                value={referencia}
                onChange={(e) => {
                  setReferencia(e.target.value);
                  setFormDirty(true); // Marcar que el formulario ha sido modificado
                }}
              />
            </div>

            <div className="group fechas" style={{ marginTop: "30px" }}>
              <div className="group">
                <label htmlFor="fecha-cotizacion">Fecha de cotización</label>
                <DatePicker
                  selected={fechaCotizacion}
                  onChange={(date) => {
                    setFechaCotizacion(date);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                  dateFormat="dd MMM yyyy"
                  placeholderText="dd MMM yyyy"
                  id="fecha-cotizacion"
                  locale="es"
                  readOnly
                />
              </div>
              <div className="group">
                <label htmlFor="fechaDeOrden" className="required">
                  Fecha de la Orden*
                </label>
                <DatePicker
                  selected={fechaDeOrden}
                  onChange={(date) => {
                    setFechaDeOrden(date);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                  dateFormat="dd MMM yyyy"
                  placeholderText="dd MMM yyyy"
                  id="fecha-de-orden"
                  locale="es"
                />
              </div>
            </div>

            <hr
              style={{
                marginBottom: "30px",
                marginTop: "20px",
                border: "1px solid #f9f9fb",
              }}
            />

            <div className="group fechas">
              <div className="group" style={{ width: "53%" }}>
                <label htmlFor="division" className="required">
                  División*
                </label>
                <select
                  id="division"
                  name="division"
                  value={division}
                  onChange={(e) => {
                    setDivision(e.target.value);
                    setFormDirty(true);
                  }}
                >
                  <option value="">Seleccionar división</option>
                  <option value="Magno Hernández">Magno Hernández</option>
                  <option value="Cesar Zuñiga">Cesar Zuñiga</option>
                </select>
              </div>
            </div>

            {/* ====== Área | Usuario ====== */}
            <div className="group fechas">
              <div className="group">
                <label htmlFor="area">Área</label>
                <input
                  type="text"
                  id="area"
                  name="area"
                  value={area}
                  onChange={(e) => {
                    setArea(e.target.value);
                    setFormDirty(true);
                  }}
                />
              </div>

              <div className="group">
                <label htmlFor="usuario">Usuario</label>
                <input
                  type="text"
                  id="usuario"
                  name="usuario"
                  value={usuario}
                  onChange={(e) => {
                    setUsuario(e.target.value);
                    setFormDirty(true);
                  }}
                />
              </div>
            </div>

            {/* ====== Asunto | Correo ====== */}
            <div className="group fechas">
              <div className="group" style={{ marginTop: "-15px" }}>
                <label htmlFor="asunto">Asunto</label>
                <input
                  type="text"
                  id="asunto"
                  name="asunto"
                  value={asunto}
                  onChange={(e) => {
                    setAsunto(e.target.value);
                    setFormDirty(true);
                  }}
                />
              </div>

              <div className="group" style={{ marginTop: "-15px" }}>
                <label htmlFor="correo">Correo</label>
                <input
                  type="email"
                  id="correo"
                  name="correo"
                  value={correo}
                  onChange={(e) => {
                    setCorreo(e.target.value);
                    setFormDirty(true);
                  }}
                />
              </div>
            </div>

            {/* ====== Atención | Planta (Planta pasa a la derecha) ====== */}
            <div className="group fechas">
              <div className="group" style={{ marginTop: "-15px" }}>
                <label htmlFor="atencion">Atención</label>
                <input
                  type="text"
                  id="atencion"
                  name="atencion"
                  value={atencion}
                  onChange={(e) => {
                    setAtencion(e.target.value);
                    setFormDirty(true);
                  }}
                />
              </div>

              <div className="group" style={{ marginTop: "-15px" }}>
                <label htmlFor="planta">Planta</label>
                <input
                  type="text"
                  id="planta"
                  name="planta"
                  value={planta}
                  onChange={(e) => {
                    setPlanta(e.target.value);
                    setFormDirty(true);
                  }}
                />
              </div>
            </div>

            {/* ====== Subtítulo + Detalles del envío (solo estos 5 campos) ====== */}
            <div className="apartado2" style={{ margin: "2px 0 6px" }}>
              Detalles del envío
            </div>

            <div className="group fechas">
              <div className="group">
                <label htmlFor="envio-metodo">Método de envío</label>
                <input
                  type="text"
                  id="envio-metodo"
                  value={envioMetodo}
                  onChange={(e) => {
                    setEnvioMetodo(e.target.value);
                    setFormDirty(true);
                  }}
                  placeholder="Ej. Paquetería, Recolección, Entrega local..."
                />
              </div>

              <div className="group">
                <label htmlFor="envio-paqueteria">Paquetería</label>
                <input
                  type="text"
                  id="envio-paqueteria"
                  value={envioPaqueteria}
                  onChange={(e) => {
                    setEnvioPaqueteria(e.target.value);
                    setFormDirty(true);
                  }}
                  placeholder="Ej. DHL, FedEx, Estafeta..."
                />
              </div>
            </div>

            <div className="group fechas">
              <div className="group">
                <label htmlFor="envio-fecha">Fecha de envío</label>
                <DatePicker
                  selected={envioFecha}
                  onChange={(date) => {
                    setEnvioFecha(date);
                    setFormDirty(true);
                  }}
                  dateFormat="dd MMM yyyy"
                  placeholderText="dd MMM yyyy"
                  id="envio-fecha"
                  locale="es"
                />
              </div>

              <div className="group">
                <label htmlFor="envio-guia">Número de guía</label>
                <input
                  type="text"
                  id="envio-guia"
                  value={envioNumeroGuia}
                  onChange={(e) => {
                    setEnvioNumeroGuia(e.target.value);
                    setFormDirty(true);
                  }}
                  placeholder="Ej. 1Z9999999999999999"
                />
              </div>
            </div>

            <div className="group">
              <label htmlFor="envio-direccion">Dirección</label>
              <textarea
                id="envio-direccion"
                value={envioDireccion}
                onChange={(e) => {
                  setEnvioDireccion(e.target.value);
                  setFormDirty(true);
                }}
                placeholder="Calle, número, colonia, ciudad, estado, C.P."
                style={{ minHeight: 60 }}
              />
            </div>

            <br />

            <div className="apartado2">Tabla de productos</div>

            <div>
              {/* Contenedor para la tabla y los botones */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  width: "100%",
                }}
              >
                {/* Tabla general */}

                <div>
                  <table className="productos-table">
                    <thead>
                      <tr>
                        <th>Detalles del Artículo</th>
                        <th style={{ textAlign: "center" }}>Modelo</th>
                        <th id="cantidad-col">Cantidad</th>
                        <th id="precio-col">Precio</th>
                        <th id="descuento-col">Descuento</th>
                        <th id="importe-col">Importe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datos.map((producto) => (
                        <tr key={producto.id} style={{ position: "relative" }}>
                          <td>
                            <div style={{ position: "relative" }}>
                              <textarea
                                className="list-products"
                                value={producto.descripcion ?? ""}
                                onChange={(e) => {
                                  const v = e.target.value;

                                  setFormDirty(true);

                                  // Mantén el dropdown funcionando, pero que NO sea el último en tocar datos
                                  handleInputChange(e, producto.id);

                                  // ÚLTIMO escritor: fija SIEMPRE la descripción actual
                                  setDatos((prev) =>
                                    prev.map((item) =>
                                      item.id === producto.id
                                        ? { ...item, descripcion: v }
                                        : item
                                    )
                                  );
                                }}
                                onBlur={() =>
                                  setTimeout(
                                    () => setFilteredProductos([]),
                                    100
                                  )
                                }
                                placeholder="Escriba para seleccionar un artículo"
                                style={{
                                  width: "600px",
                                  height: "130px",
                                  padding: "8px",
                                  fontSize: "14px",
                                  lineHeight: "1.5",
                                  resize: "vertical",
                                  overflow: "auto",
                                  whiteSpace: "pre-wrap",
                                  fontFamily: "Arial, sans-serif",
                                }}
                              />
                              {activeRow === producto.id &&
                                filteredProductos.length > 0 && (
                                  <ul
                                    style={{
                                      position: "absolute",
                                      top: "105%",
                                      left: 0,
                                      width: "100%",
                                      backgroundColor: "#fff",
                                      border: "1px solid #cecece",
                                      borderRadius: "5px",
                                      zIndex: 10,
                                      maxHeight: "150px",
                                      overflowY: "auto",
                                      listStyle: "none",
                                      padding: "5px",
                                      margin: 0,
                                    }}
                                  >
                                    {filteredProductos.map((prod) => (
                                      <li
                                        key={prod.id}
                                        onMouseDown={() => {
                                          setDatos((prev) =>
                                            prev.map((item) =>
                                              item.id === producto.id
                                                ? {
                                                    ...item,
                                                    descripcion: prod.nombre,
                                                    nombre: prod.nombre,
                                                  } // sincroniza ambos
                                                : item
                                            )
                                          );

                                          setFilteredProductos([]); // cierra el dropdown
                                          setFormDirty(true);
                                        }}
                                        style={{
                                          padding: "5px",
                                          cursor: "pointer",
                                          backgroundColor: "#f9f9f9",
                                          marginBottom: "2px",
                                        }}
                                      >
                                        {prod.nombre}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                            </div>
                          </td>

                          <td>
                            <input
                              type="text"
                              value={
                                producto.modelo ||
                                producto.numeroDeParteCliente ||
                                ""
                              }
                              onChange={(e) => {
                                const modelo = e.target.value;
                                setDatos((prev) =>
                                  prev.map((item) =>
                                    item.id === producto.id
                                      ? { ...item, modelo }
                                      : item
                                  )
                                );
                                setFormDirty(true);
                              }}
                            />
                          </td>

                          <td id="cantidad-col">
                            <input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              value={(producto.cantidad ?? "").toString()}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (!/^\d*$/.test(v)) return;

                                const qty = v === "" ? "" : parseInt(v, 10);

                                setDatos((prev) =>
                                  prev.map((item) =>
                                    item.id === producto.id
                                      ? {
                                          ...item,
                                          cantidad: qty,
                                          importe:
                                            (item.tarifa || 0) * (qty || 0),
                                        }
                                      : item
                                  )
                                );
                                setFormDirty(true);
                              }}
                              onBlur={(e) => {
                                const v = e.target.value;
                                const qty = v === "" ? 0 : parseInt(v, 10);
                                setDatos((prev) =>
                                  prev.map((item) =>
                                    item.id === producto.id
                                      ? {
                                          ...item,
                                          cantidad: qty,
                                          importe: (item.tarifa || 0) * qty,
                                        }
                                      : item
                                  )
                                );
                              }}
                              style={{
                                width: "100%",
                                textAlign: "right",
                                padding: "8px",
                                fontSize: "14px",
                                borderRadius: "4px",
                              }}
                            />
                          </td>

                          <td id="precio-col">
                            <input
                              type="number"
                              inputMode="decimal"
                              step="0.01"
                              min="0"
                              value={producto.tarifa ?? 0}
                              onFocus={(e) => e.target.select()} // ← escribir sin borrar
                              onChange={(e) => {
                                const v = e.target.value;
                                const price =
                                  v === ""
                                    ? 0
                                    : parseFloat(v.replace(",", "."));
                                setDatos((prev) =>
                                  prev.map((item) =>
                                    item.id === producto.id
                                      ? {
                                          ...item,
                                          tarifa: isNaN(price) ? 0 : price,
                                          importe:
                                            (isNaN(price) ? 0 : price) *
                                            (item.cantidad || 0),
                                        }
                                      : item
                                  )
                                );
                                setFormDirty(true);
                              }}
                              onBlur={(e) => {
                                const num = parseFloat(e.target.value) || 0;
                                setDatos((prev) =>
                                  prev.map((item) =>
                                    item.id === producto.id
                                      ? {
                                          ...item,
                                          tarifa: num,
                                          importe: num * (item.cantidad || 0),
                                        }
                                      : item
                                  )
                                );
                              }}
                              style={{
                                width: "100%",
                                textAlign: "right",
                                padding: "8px",
                                fontSize: "14px",
                                borderRadius: "4px",
                              }}
                            />
                          </td>

                          <td id="descuento-col">
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                height: "32px",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                overflow: "hidden",
                                width: "100%",
                              }}
                            >
                              {/* Input de descuento (ahora acepta decimales con . o ,) */}
                              <input
                                type="number"
                                inputMode="decimal"
                                step="0.01"
                                min="0"
                                value={(producto.descuento ?? 0).toString()}
                                onChange={(e) => {
                                  // Acepta "10.5" o "10,5"
                                  const raw = e.target.value.replace(",", ".");
                                  // Permite escribir números decimales de forma progresiva
                                  if (!/^(\d+(\.\d*)?|\.\d*)?$/.test(raw))
                                    return;

                                  let d = parseFloat(raw);
                                  if (isNaN(d)) d = 0;

                                  // Si es porcentaje, tope en 100
                                  const tipo = producto.tipoDescuento || "%";
                                  if (tipo === "%" && d > 100) d = 100;

                                  setDatos((prev) =>
                                    prev.map((item) => {
                                      if (item.id !== producto.id) return item;
                                      const qty =
                                        parseFloat(item.cantidad) || 0;
                                      const price =
                                        parseFloat(item.tarifa) || 0;
                                      const importe =
                                        tipo === "%"
                                          ? price * qty * (1 - d / 100)
                                          : Math.max(price * qty - d, 0);
                                      return { ...item, descuento: d, importe };
                                    })
                                  );
                                  setFormDirty(true);
                                }}
                                style={{
                                  flex: 1,
                                  border: "none",
                                  outline: "none",
                                  textAlign: "right",
                                  padding: "0 12px",
                                  fontSize: "16px",
                                  lineHeight: "32px",
                                }}
                              />

                              {/* Select de tipo con icono */}
                              <div
                                style={{
                                  position: "relative",
                                  width: "50px",
                                  height: "100%",
                                }}
                              >
                                <select
                                  value={producto.tipoDescuento || "%"}
                                  onChange={(e) => {
                                    const tipo = e.target.value;
                                    setDatos((prev) =>
                                      prev.map((item) => {
                                        if (item.id !== producto.id)
                                          return item;
                                        const qty =
                                          parseFloat(item.cantidad) || 0;
                                        const price =
                                          parseFloat(item.tarifa) || 0;
                                        const d =
                                          parseFloat(item.descuento) || 0;
                                        const importe =
                                          tipo === "%"
                                            ? price * qty * (1 - d / 100)
                                            : Math.max(price * qty - d, 0);
                                        return {
                                          ...item,
                                          tipoDescuento: tipo,
                                          importe,
                                        };
                                      })
                                    );
                                    setFormDirty(true);
                                  }}
                                  style={{
                                    appearance: "none",
                                    WebkitAppearance: "none",
                                    MozAppearance: "none",
                                    width: "100%",
                                    height: "100%",
                                    border: "none",
                                    outline: "none",
                                    padding: "0 12px",
                                    fontSize: "16px",
                                    lineHeight: "32px",
                                    backgroundColor: "#f4f4f4",
                                    textAlign: "center",
                                    cursor: "pointer",
                                  }}
                                >
                                  <option value="%">%</option>
                                  <option value="$">$</option>
                                </select>
                                <i
                                  style={{
                                    position: "absolute",
                                    right: "8px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    pointerEvents: "none",
                                    color: "#666",
                                    fontSize: "12px",
                                  }}
                                />
                              </div>
                            </div>
                          </td>

                          <td
                            id="importe-col"
                            style={{ textAlign: "right", paddingRight: "10px" }}
                          >
                            $
                            {parseFloat(producto.importe || 0).toLocaleString(
                              "en-US",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )}
                          </td>

                          {/* Botones de acción */}
                          <div
                            style={{
                              position: "absolute",
                              right: "-75px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              display: "flex",
                            }}
                          >
                            <button
                              type="button"
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                              }}
                            >
                              <i
                                className="bi bi-three-dots-vertical"
                                style={{ fontSize: "1.2rem" }}
                              />
                            </button>
                            <button
                              type="button"
                              style={{
                                background: "none",
                                border: "none",
                                color: "#dc3545",
                                cursor: "pointer",
                              }}
                              onClick={() => eliminar(producto.id)}
                            >
                              <i
                                className="bi bi-x-lg"
                                style={{ fontSize: "1.2rem" }}
                              />
                            </button>
                          </div>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="acciones-tabla">
              <button type="button" onClick={añadirFila}>
                <i
                  style={{ color: "#007bff", fontSize: "16px" }}
                  className="bi bi-plus-circle-fill"
                ></i>
                Añadir nueva fila
              </button>
              <button type="button" onClick={() => agregarBulk(datos)}>
                <i
                  style={{ color: "#007bff", fontSize: "16px" }}
                  className="bi bi-plus-circle-fill"
                ></i>
                Agregar artículos a granel
              </button>
            </div>

            <div
              className="data-container"
              style={{
                display: "flex",
                justifyContent: "flex-end",
                paddingRight: "80px",
              }}
            >
              {/* <div className="notes-container">
                <label htmlFor="customer-notes">Notas del Cliente</label>
                <textarea
                  id="customer-notes"
                  placeholder="Escribe tus notas aquí..."
                  value={notas}
                  onChange={(e) => {
                    setNotas(e.target.value);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                ></textarea>
              </div> */}

              <div className="totales-container">
                {/* Subtotal (antes del descuento global) */}
                <div className="row">
                  <span className="label">Subtotal</span>
                  <span>
                    $
                    {subtotal.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>

                {/* Descuento: input + select (% | MXN) */}
                <div className="row descuento">
                  <span className="label">Descuento</span>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      overflow: "visible",
                      height: 40,
                      minWidth: 180,
                      background: "#fff",
                    }}
                  >
                    <input
                      type="text"
                      inputMode="decimal"
                      value={descuento.toString()}
                      onChange={(e) => {
                        const v = e.target.value.replace(",", "."); // acepta "," o "."
                        // permite vacío o números decimales progresivos
                        if (!/^(\d+(\.\d*)?|\.\d*)?$/.test(v) && v !== "")
                          return;

                        setDescuento(v === "" ? "" : v); // permite escribir sin bloquear
                      }}
                      onBlur={(e) => {
                        let d = parseFloat(e.target.value) || 0;
                        if (tipoDescuentoGlobal === "%" && d > 100) d = 100;
                        if (tipoDescuentoGlobal === "MXN" && d > subtotal)
                          d = subtotal;
                        if (d < 0) d = 0;
                        setDescuento(d);
                      }}
                      style={{
                        flex: 1,
                        border: "none",
                        outline: "none",
                        textAlign: "right",
                        padding: "0 12px",
                        fontSize: 16,
                        height: "100%",
                        background: "transparent",
                      }}
                    />

                    <MiniSelect
                      value={tipoDescuentoGlobal}
                      options={[
                        { value: "%", label: "%" },
                        { value: "MXN", label: "MXN" },
                      ]}
                      onChange={(next) => {
                        setTipoDescuentoGlobal(next);
                        setDescuento((prev) => {
                          let d = parseFloat(prev) || 0;
                          if (next === "%" && d > 100) d = 100;
                          if (next === "MXN" && d > subtotal) d = subtotal;
                          if (d < 0) d = 0;
                          return d;
                        });
                      }}
                    />
                  </div>

                  <span className="discount-percentage">
                    – $
                    {descuentoAplicado.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>

                {/* IVA calculado sobre el subtotal con descuento */}
                <div className="row iva">
                  <span className="label">
                    IVA ({(ivaRate * 100).toFixed(0)}%)
                  </span>
                  <span>
                    $
                    {ivaLocal.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>

                <hr />

                {/* Total final */}
                <div className="row total">
                  <span>Total ({divisa})</span>
                  <span className="total-amount">
                    $
                    {totalLocal.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </div>
            </div>

            <div
              style={{
                backgroundColor: "#f9f9fb",
                padding: "8px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "10px",
                width: "94%",
                marginTop: "30px",
                paddingBottom: "30px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              }}
            >
              {/* Left Section: Términos y Condiciones */}
              <div className="terminos-container">
                <h4>Términos y condiciones</h4>
                <textarea
                  value={terminos}
                  onChange={(e) => {
                    setTerminos(e.target.value);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                />
              </div>

              {/* Right Section: File Upload */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "8px",
                  flex: "0 0 530px",
                  marginTop: "30px",
                }}
              >
                <label style={{ fontSize: "14px" }}>
                  Adjuntar archivo(s) a Estimación
                </label>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    border: "1px dashed #ccc",
                    borderRadius: "5px",
                    overflow: "hidden", // Eliminar cualquier contenido que se salga del borde
                  }}
                >
                  <label
                    style={{
                      padding: "8px 10px",
                      backgroundColor: "#fff",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                      whiteSpace: "nowrap", // Evitar que el contenido del botón se ajuste en varias líneas
                    }}
                  >
                    <i style={{ color: "#000" }} className="bi bi-upload"></i>
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#000",
                        fontWeight: "lighter",
                      }}
                    >
                      Cargar archivo
                    </span>
                    <input
                      type="file"
                      style={{ display: "none" }} // Ocultar el input de archivo
                      accept=".csv,.xls,.xlsx,.doc,.docx,.pdf,.jpg,.jpeg,.png,.svg,.txt"
                      onChange={manejarArchivos}
                    />
                  </label>

                  <div
                    style={{
                      padding: "10px",
                      backgroundColor: "#fff",
                      borderLeft: "1px dashed #ccc", // Borde que conecta con el input
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      borderTopRightRadius: "auto", // Bordes redondeados en la esquina derecha
                      borderBottomRightRadius: "auto",
                      minWidth: "20px", // Evitar que el botón se haga demasiado pequeño
                      margin: "0", // Eliminar cualquier margen que agregue espacio extra
                    }}
                  >
                    <i
                      style={{
                        color: "#000",
                        fontSize: "14px", // Ajustar el tamaño del ícono
                      }}
                      className="bi bi-chevron-down"
                    ></i>
                  </div>
                </div>

                <p style={{ fontSize: "12px", color: "#888" }}>
                  Puedes cargar un máximo de 10 archivos, cada uno de 10 MB
                </p>

                {cargando && (
                  <p style={{ fontSize: "14px", color: "#888" }}>
                    Cargando archivo...
                  </p>
                )}

                {archivos.length > 0 && (
                  <div style={{ width: "100%" }}>
                    <strong>Archivos cargados:</strong>
                    <ul
                      style={{
                        listStyle: "none",
                        padding: 0,
                        marginTop: "10px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      {archivos.map(({ nombre, url }) => {
                        const ext = nombre.split(".").pop().toLowerCase();
                        const { icon, color } = obtenerIcono(ext);

                        return (
                          <li
                            key={nombre}
                            style={
                              {
                                /* estilos */
                              }
                            }
                          >
                            <i
                              className={`bi ${icon}`}
                              style={{
                                color,
                                fontSize: "1.5rem",
                                flexShrink: 0,
                              }}
                            />
                            <span style={{ flexGrow: 1 }}>{nombre}</span>

                            {/* Descargar */}
                            <button
                              type="button" // ← IMPORTANTE
                              onClick={(e) => {
                                e.preventDefault(); // ← evita submit
                                e.stopPropagation();
                                descargarArchivo(url, nombre);
                              }}
                              style={{
                                color: "#007bff",
                                background: "none",
                                border: "none",
                                fontWeight: "bold",
                                cursor: "pointer",
                              }}
                            >
                              <i
                                className="bi bi-download"
                                style={{ fontSize: "20px" }}
                              />
                            </button>

                            {/* Eliminar */}
                            <button
                              type="button" // ← IMPORTANTE
                              onClick={(e) => {
                                e.preventDefault(); // ← evita submit
                                e.stopPropagation();
                                eliminarArchivo(nombre);
                              }}
                              style={{
                                border: "none",
                                background: "transparent",
                                color: "#dc3545",
                                cursor: "pointer",
                              }}
                            >
                              <i
                                className="bi bi-trash"
                                style={{ fontSize: "20px" }}
                              />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Grupo de botones: Guardar como borrador, Guardar y enviar, Cancelar */}
          <div className="cotizacion-toolbar">
            <div className="btn-group">
              <button
                id="guardar-enviar"
                className="btn btn-primary"
                type="submit"
                onClick={guardar}
              >
                {modoEdicion ? "Guardar" : "Guardar y enviar"}
              </button>
              <button
                id="cancelar"
                className="btn btn-secondary"
                type="button"
                onClick={handleCancel}
              >
                Cancelar
              </button>
            </div>
          </div>
        </form>

        <Modal
          isOpen={modalIsOpen}
          onRequestClose={cerrarModalPrevia}
          contentLabel="Vista Previa"
          style={customStyles}
        >
          {mostrarPrevia && (
            <PreviaPedidos
              cliente={cliente}
              clientes={clientes}
              orden={orden}
              numeroOrden={numeroOrdenDeCompra}
              referencia={referencia}
              asunto={asunto}
              fechaCotizacion={fechaCotizacion}
              productosSeleccionados={datos}
              subtotalConDescuento={subtotalConDescLocal}
              iva={ivaLocal}
              total={totalLocal}
              descuento={descuento}
              descuentoTipo={tipoDescuentoGlobal}
              continuarDesdePrevia={continuarDesdePrevia}
              numeroCotizacion={numeroCotizacion}
              fechaOrden={fechaDeOrden}
              clienteId={cliente}
              usuario={usuario}
              area={area}
              atencion={atencion}
              correo={correo}
              planta={planta}
              division={division}
              estado={orden?.estado || "Pendiente"}
              envio={{
                metodo: envioMetodo,
                paqueteria: envioPaqueteria,
                fechaEnvio: envioFecha,
                numeroGuia: envioNumeroGuia,
                direccion: envioDireccion,
              }}
            />
          )}
          <div className="modal-buttons">
            <button onClick={cerrarModalPrevia} className="eliminarBtnModal">
              Cerrar
            </button>
          </div>
        </Modal>

        {mostrarTabla && (
          <TablaPedidos
            numeroOrdenDeCompra={numeroOrdenDeCompra}
            ordenes={ordenes}
            setOrdenes={setOrdenes}
          />
        )}
      </div>
    </>
  );
}

export default PedidoForm;
